import { useLocation } from "react-router-dom";

import {
  useBalances,
  useDebounce,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
  useReactRouterLink,
} from "../../../hooks";
import {
  ICreateMerchantDedicatedTopUpAccount,
  IHookForm,
  IModalFormWithTableProps,
  IFormWithTableProps,
  IViewBalance,
  IViewMerchantDedicatedTopUpAccount,
  IBank,
  NoMeta,
  IModalFormProps,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";

import { View } from "./View";
import { createMerchantDedicatedTopUpAccountSchema } from "./validationSchema";

interface IQueryParams {
  country: string;
  currency: string;
  gateway_code?: string | null;
}

export interface ICreateMerchantDedicatedTopUpAccountFormViewProps
  extends IHookForm<ICreateMerchantDedicatedTopUpAccount>,
    IModalFormProps {
  // IModalFormWithTableProps
  isLoadingBalances: boolean;
  balances: IViewBalance[];

  isLoadingPartnerBankList: boolean;
  partnerBankList: IBank[];
  formValues: ICreateMerchantDedicatedTopUpAccount;
}

export const CreateMerchantDedicatedTopUpAccountForm = () => {
  const { navigate } = useReactRouterLink();
  const location = useLocation();

  const { isLoadingBalances, balances } = useBalances();

  const closeModal = () => {
    navigate(location.pathname.replace("/top-up-accounts/create", ""));
  };

  const {
    isSuccess: isSuccessCreateMerchantDedicatedTopUpAccount,
    isLoading: isLoadingCreateMerchantDedicatedTopUpAccount,
    isError: isErrorCreateMerchantDedicatedTopUpAccount,
    mutate: createMerchantDedicatedTopUpAccount,
    data: newlyCreatedTopUpAccount,
  } = useMerchantApiMutation<
    ICreateMerchantDedicatedTopUpAccount,
    IViewMerchantDedicatedTopUpAccount
  >("createMerchantDedicatedTopUpAccount", [
    "getMerchantDedicatedTopUpAccounts",
  ]).usePostMutation;

  const initialFormValues: ICreateMerchantDedicatedTopUpAccount = {
    country: "NG",
    currency: "NGN",
    // virtual_account_name: null,
    account_reference: null,
    preferred_bank_code: "",
    bvn: null,
    nin: null,
    gateway_code: null,
    callback_url: null,
  };

  const handleApiSubmit = (data: ICreateMerchantDedicatedTopUpAccount) => {
    if (window.confirm("Are you sure you want to create a new top-up account?"))
      createMerchantDedicatedTopUpAccount(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, watch } = useHookForm({
    initialFormValues,
    validationSchema: createMerchantDedicatedTopUpAccountSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  const debouncePeriodElapsed = useDebounce();
  const { isLoading: isLoadingBankList, data: bankList } =
    useMerchantApiQueryWithParams<IQueryParams, IBank[], NoMeta>(
      [
        "getPartnerBanks",
        {
          country: formValues.country,
          currency: formValues.currency,
          gateway_code: formValues.gateway_code,
        },
      ],
      {
        enabled:
          debouncePeriodElapsed &&
          !!formValues.country &&
          !!formValues.currency,
      }
    );

  return (
    <View
      isLoadingBalances={isLoadingBalances}
      balances={balances}
      isLoadingPartnerBankList={isLoadingBankList}
      partnerBankList={bankList || []}
      formValues={formValues}
      isModalOpen
      closeModal={closeModal}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
